<template>
  <div class="select-wrapper">
    <label v-if="label" :for="selectId" class="block text-sm font-medium text-text text-left mb-1">
      {{ label }}
      <span v-if="required" class="text-error ml-1">*</span>
    </label>
    
    <div class="relative">
      <select
        :id="selectId"
        :name="name"
        :value="modelValue"
        :disabled="disabled"
        :required="required"
        :aria-label="props.ariaLabel"
        :aria-describedby="describedBy"
        :class="['select-field', `state-${state}`]"
        @change="$emit('update:modelValue', ($event.target as HTMLSelectElement).value)"
        @focus="$emit('focus', $event)"
        @blur="$emit('blur', $event)"
      >
        <option v-if="showPlaceholderOption" value="" disabled>{{ placeholderOptionLabel }}</option>
        <option
          v-for="option in normalizedOptions"
          :key="option.value"
          :value="option.value"
          :disabled="option.disabled"
        >
          {{ option.label }}
        </option>
      </select>
      
      <!-- Custom dropdown arrow -->
      <div class="select-arrow">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
    
    <div v-if="helpText && !errorText" :id="`${selectId}-help`" class="mt-1 text-sm text-text-muted">
      {{ helpText }}
    </div>
    
    <div v-if="errorText" :id="`${selectId}-error`" class="mt-1 text-sm text-error">
      {{ errorText }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import { WEEKLY_CHECK_IN_SUPPRESS_PLACEHOLDERS } from '@/constants/weeklyCheckInPrintContext'

export interface SelectOption {
  value: string | number
  label: string
  disabled?: boolean
}

interface Props {
  modelValue?: string | number | null
  options: SelectOption[] | string[]
  state?: 'default' | 'success' | 'warning' | 'danger'
  disabled?: boolean
  required?: boolean
  placeholder?: string
  label?: string
  helpText?: string
  errorText?: string
  ariaLabel?: string
  ariaDescribedby?: string
  name?: string
}

const props = withDefaults(defineProps<Props>(), {
  state: 'default',
  disabled: false,
  required: false
})

const suppressPrintPlaceholders = inject(WEEKLY_CHECK_IN_SUPPRESS_PLACEHOLDERS, false)

const showPlaceholderOption = computed(() => Boolean(props.placeholder))

const placeholderOptionLabel = computed(() => {
  if (!props.placeholder) return ''
  return suppressPrintPlaceholders ? '\u00a0' : props.placeholder
})

defineEmits<{
  'update:modelValue': [value: string | number | null]
  focus: [event: FocusEvent]
  blur: [event: FocusEvent]
}>()

// Generate unique ID for accessibility
const selectId = computed(() => `select-${Math.random().toString(36).substring(2, 11)}`)

// Normalize options to SelectOption format
const normalizedOptions = computed(() => {
  return props.options.map(option => {
    if (typeof option === 'string') {
      return { value: option, label: option }
    }
    return option
  })
})

// Computed aria-describedby for accessibility
const describedBy = computed(() => {
  const ids = []
  if (props.helpText && !props.errorText) ids.push(`${selectId.value}-help`)
  if (props.errorText) ids.push(`${selectId.value}-error`)
  return ids.length > 0 ? ids.join(' ') : undefined
})
</script>

<style scoped>
.select-wrapper {
  position: relative;
}

.select-field {
  width: 100%;
  padding: 0.5rem 2.5rem 0.5rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  border: 1px solid;
  border-radius: 0.375rem;
  
  appearance: none;
  cursor: pointer;
  
  /* Use theme variables */
  background-color: var(--color-input-background);
  border-color: var(--color-input-border);
  color: var(--color-input-text);
}

.select-field:focus {
  outline: none;
  border-color: var(--color-input-focus);
  box-shadow: 0 0 0 2px var(--color-input-focus);
}

.select-field:disabled {
  background-color: var(--color-surface-muted);
  color: var(--color-text-muted);
  cursor: not-allowed;
}

.select-field option {
  background-color: var(--color-input-background);
  color: var(--color-input-text);
}

.select-field option:disabled {
  color: var(--color-text-muted);
}

/* Custom arrow */
.select-arrow {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--color-text-muted);
}

.select-field:disabled ~ .select-arrow {
  opacity: 0.5;
}

/* State variants */
.select-field.state-success {
  border-color: var(--color-success);
}

.select-field.state-success:focus {
  border-color: var(--color-success);
  box-shadow: 0 0 0 2px var(--color-success);
}

.select-field.state-warning {
  border-color: var(--color-warning);
}

.select-field.state-warning:focus {
  border-color: var(--color-warning);
  box-shadow: 0 0 0 2px var(--color-warning);
}

.select-field.state-danger {
  border-color: var(--color-danger);
}

.select-field.state-danger:focus {
  border-color: var(--color-danger);
  box-shadow: 0 0 0 2px var(--color-danger);
}
</style>
